import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { Formik, FormikHelpers } from 'formik';
import { boolean, object, string } from 'yup';
import { onSnapshot } from 'firebase/firestore';
import { usersCollectionRef } from '../../lib/firebase';
import { createUser, updateUser, useActor } from '../../lib/audited-writes';
import { useToast } from '../../lib/toast-context';

const UID_EXISTS_ERROR = 'UID_EXISTS';

// Permissive shape — `users-context.tsx` only reads `enabled` and `admin`,
// so the other fields (email, name) are best-effort.
type AppUser = {
  ref: string;
  email?: string;
  name?: string;
  enabled?: boolean;
  admin?: boolean;
  canMoveAttendees?: boolean;
};

const NAME_MAX_LENGTH = 100;
const UID_MAX_LENGTH = 128;

const primaryLabel = (user: AppUser): string => user.name || user.ref;

type UserFormValues = {
  uid: string;
  name: string;
  email: string;
  admin: boolean;
  enabled: boolean;
  canMoveAttendees: boolean;
};

const blankFormValues: UserFormValues = {
  uid: '',
  name: '',
  email: '',
  admin: false,
  enabled: true,
  canMoveAttendees: false,
};

const buildUserSchema = (existingUids: string[], isEdit: boolean) =>
  object().shape({
    uid: string()
      .trim()
      .required('UID is required')
      .max(UID_MAX_LENGTH, `UID must be ${UID_MAX_LENGTH} characters or less`)
      .test(
        'unique',
        'A user with this UID already exists',
        (v) => isEdit || !v || !existingUids.includes(v.trim())
      ),
    name: string()
      .trim()
      .required('Name is required')
      .max(
        NAME_MAX_LENGTH,
        `Name must be ${NAME_MAX_LENGTH} characters or less`
      ),
    email: string().trim().email('Must be a valid email').max(254, 'Too long'),
    admin: boolean(),
    enabled: boolean(),
    canMoveAttendees: boolean(),
  });

const UserFormDialog = ({
  open,
  user,
  existingUids,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean;
  user: AppUser | null;
  existingUids: string[];
  onClose: () => void;
  onSaved: (mode: 'add' | 'edit') => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const isEdit = user !== null;
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const initialValues = useMemo<UserFormValues>(
    () =>
      user
        ? {
            uid: user.ref,
            name: user.name || '',
            email: user.email || '',
            admin: Boolean(user.admin),
            enabled: user.enabled !== false,
            canMoveAttendees: Boolean(user.canMoveAttendees),
          }
        : blankFormValues,
    [user]
  );

  const validationSchema = useMemo(
    () => buildUserSchema(existingUids, isEdit),
    [existingUids, isEdit]
  );

  const handleSubmit = async (
    values: UserFormValues,
    { setSubmitting }: FormikHelpers<UserFormValues>
  ) => {
    setIsWorking(true);
    let succeeded = false;
    try {
      const uid = values.uid.trim();
      const name = values.name.trim();
      const email = values.email.trim();

      const payload = {
        name,
        admin: values.admin,
        enabled: values.enabled,
        canMoveAttendees: values.canMoveAttendees,
        ...(email ? { email } : {}),
      };

      if (isEdit && user) {
        await updateUser(actor, user, payload);
      } else {
        await createUser(actor, uid, payload);
      }
      succeeded = true;
    } catch (error) {
      if (error instanceof Error && error.message === UID_EXISTS_ERROR) {
        onError('A user with this UID already exists');
      } else {
        console.error('Error saving user', error);
        onError(isEdit ? 'Failed to update user' : 'Failed to add user');
      }
    } finally {
      setSubmitting(false);
      setIsWorking(false);
    }
    if (succeeded) {
      onSaved(isEdit ? 'edit' : 'add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{isEdit ? 'Edit user' : 'Add user'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Stack spacing={2}>
                <TextField
                  name='uid'
                  label='UID'
                  value={values.uid}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.uid && Boolean(errors.uid)}
                  helperText={
                    (touched.uid && errors.uid) ||
                    (isEdit
                      ? 'UID cannot be changed'
                      : 'Firebase Auth UID for this user')
                  }
                  inputProps={{ maxLength: UID_MAX_LENGTH }}
                  disabled={isSubmitting || isEdit}
                  fullWidth
                />
                <TextField
                  name='name'
                  label='Name'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={
                    (touched.name && errors.name) ||
                    `${values.name.length}/${NAME_MAX_LENGTH}`
                  }
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                  disabled={isSubmitting}
                  fullWidth
                />
                <TextField
                  name='email'
                  label='Email (optional)'
                  type='email'
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  inputProps={{ maxLength: 254 }}
                  disabled={isSubmitting}
                  fullWidth
                />
                <Stack direction='row' spacing={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        name='enabled'
                        checked={values.enabled}
                        onChange={(_, checked) =>
                          setFieldValue('enabled', checked)
                        }
                        disabled={isSubmitting}
                      />
                    }
                    label='Enabled'
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        name='admin'
                        checked={values.admin}
                        onChange={(_, checked) =>
                          setFieldValue('admin', checked)
                        }
                        disabled={isSubmitting}
                      />
                    }
                    label='Admin'
                  />
                </Stack>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        name='canMoveAttendees'
                        checked={values.canMoveAttendees}
                        onChange={(_, checked) =>
                          setFieldValue('canMoveAttendees', checked)
                        }
                        disabled={isSubmitting}
                      />
                    }
                    label='Can move attendees'
                  />
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: -0.5, ml: 4.5 }}
                  >
                    Allow this user to move attendees between groups on the
                    Roster.
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={isSubmitting}
              >
                {isEdit ? 'Save' : 'Add user'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const UsersList = () => {
  const toast = useToast();
  const [users, setUsers] = useState<AppUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      usersCollectionRef,
      (snapshot) => {
        const res: AppUser[] = [];
        snapshot.forEach((doc) => {
          res.push({ ref: doc.id, ...doc.data() } as AppUser);
        });
        setUsers(res);
        setError(null);
      },
      (err) => {
        console.error('Error loading users', err);
        setError('Failed to load users');
        setUsers([]);
      }
    );
    return unsubscribe;
  }, []);

  const sortedUsers = useMemo(() => {
    if (!users) return null;
    return [...users].sort((a, b) =>
      primaryLabel(a).localeCompare(primaryLabel(b), undefined, {
        sensitivity: 'base',
      })
    );
  }, [users]);

  const existingUids = useMemo(() => (users || []).map((u) => u.ref), [users]);

  const renderBody = () => {
    if (error) {
      return (
        <Typography color='error' textAlign='center'>
          {error}
        </Typography>
      );
    }

    if (sortedUsers === null) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (sortedUsers.length === 0) {
      return (
        <Typography color='text.secondary' textAlign='center'>
          No users yet. Click <strong>Add user</strong> to add one.
        </Typography>
      );
    }

    return (
      <List dense disablePadding>
        {sortedUsers.map((user) => {
          const primary = primaryLabel(user);
          const showEmail = Boolean(user.email);
          return (
            <ListItem
              key={user.ref}
              divider
              sx={{
                py: 0.5,
                px: 1,
                gap: 1,
                alignItems: 'center',
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ fontWeight: 500, flexShrink: 0 }}
                  noWrap
                >
                  {primary}
                </Typography>
                {showEmail && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    noWrap
                    sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {user.email}
                  </Typography>
                )}
              </Box>
              <Stack
                direction='row'
                spacing={0.5}
                sx={{ flexShrink: 0 }}
              >
                {user.admin && (
                  <Chip label='Admin' size='small' color='primary' />
                )}
                {user.enabled === false && (
                  <Chip label='Disabled' size='small' color='default' />
                )}
                {user.enabled !== false && !user.admin && (
                  <Chip label='Enabled' size='small' color='success' />
                )}
              </Stack>
              <IconButton
                size='small'
                aria-label={`Edit ${primary}`}
                onClick={() => setEditing(user)}
                sx={{ flexShrink: 0 }}
              >
                <Edit fontSize='small' />
              </IconButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setAdding(true)}
        >
          Add user
        </Button>
      </Box>
      {renderBody()}

      <UserFormDialog
        open={adding || editing !== null}
        user={editing}
        existingUids={existingUids}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={(mode) =>
          toast.success(mode === 'add' ? 'User added' : 'User saved')
        }
        onError={(msg) => toast.error(msg)}
      />
    </>
  );
};

export default UsersList;
